import React, { useEffect } from "react";
import strings from "translate/language";
import "./JustoPasswordResetModal.css";

/**
 * Confirm single-user force logout (session_hash only — never password).
 */
const JustoForceLogoutModal = ({
  open,
  onClose,
  onConfirm,
  userName = "",
  email = "",
  mobile = "",
  submitting = false,
}) => {
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, onClose]);

  if (!open) {
    return null;
  }

  const displayName = String(userName || "").trim() || strings.notavailable;
  const displayEmail = String(email || "").trim();
  const displayMobile = String(mobile || "").trim();
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <div
      className="justo-pwr-modal__backdrop"
      onClick={() => !submitting && onClose()}
      role="presentation"
    >
      <div
        className="justo-pwr-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="justo-force-logout-title"
      >
        <div className="justo-pwr-modal__header">
          <div className="justo-pwr-modal__icon" aria-hidden="true">
            <i className="fa fa-sign-out" />
          </div>
          <div className="justo-pwr-modal__header-copy">
            <h2
              className="justo-pwr-modal__title"
              id="justo-force-logout-title"
            >
              {strings.forceLogoutUserTitle}
            </h2>
            <p className="justo-pwr-modal__subtitle">
              {strings.forceLogoutUserIntro}
            </p>
          </div>
          <button
            type="button"
            className="justo-pwr-modal__close"
            onClick={() => !submitting && onClose()}
            aria-label="Close"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        <div className="justo-pwr-modal__body">
          <div className="justo-pwr-modal__col justo-pwr-modal__col--user">
            <p className="justo-pwr-modal__section-label">
              {strings.resetPasswordUserLabel}
            </p>
            <div className="justo-pwr-modal__user">
              <div className="justo-pwr-modal__avatar" aria-hidden="true">
                {initials || "?"}
              </div>
              <div className="justo-pwr-modal__user-meta">
                <p className="justo-pwr-modal__user-name">{displayName}</p>
                <div className="justo-pwr-modal__user-lines">
                  <span className="justo-pwr-modal__user-line">
                    <i className="fa fa-envelope-o" aria-hidden="true" />
                    {displayEmail || strings.notavailable}
                  </span>
                  <span className="justo-pwr-modal__user-line">
                    <i className="fa fa-phone" aria-hidden="true" />
                    {displayMobile || strings.notavailable}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="justo-pwr-modal__col justo-pwr-modal__col--details">
            <p className="justo-pwr-modal__section-label">
              {strings.resetPasswordWhatHappens}
            </p>
            <ul className="justo-pwr-modal__steps">
              <li className="justo-pwr-modal__step">
                <span className="justo-pwr-modal__step-icon" aria-hidden="true">
                  <i className="fa fa-sign-out" />
                </span>
                <span>{strings.forceLogoutUserWarningSession}</span>
              </li>
              <li className="justo-pwr-modal__step">
                <span
                  className="justo-pwr-modal__step-icon justo-pwr-modal__step-icon--ok"
                  aria-hidden="true"
                >
                  <i className="fa fa-lock" />
                </span>
                <span>{strings.forceLogoutUserWarningPassword}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="justo-pwr-modal__footer">
          <button
            type="button"
            className="justo-pwr-modal__btn justo-pwr-modal__btn--ghost"
            onClick={() => !submitting && onClose()}
            disabled={submitting}
          >
            {strings.cancel}
          </button>
          <button
            type="button"
            className="justo-pwr-modal__btn justo-pwr-modal__btn--solid"
            onClick={() => onConfirm()}
            disabled={submitting}
          >
            <i className="fa fa-sign-out" aria-hidden="true" />
            {submitting
              ? strings.loggingOut
              : strings.forceLogoutUserConfirmAction}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JustoForceLogoutModal;
